import type { MeResponse, UserPreferences } from "@starsach/shared-types";

/**
 * The information architecture (requirements.md section 8.9, 2026-09-18):
 * modules everyone gets, then the organisation's own two, then Gairm's
 * two for staff. Journeys and Flow configuration are product areas with
 * sections of their own; the nav expands the active one. Every module
 * except Focus, Customers, Platform and Docs is about one environment,
 * carried in the path so a link says which.
 */
export type ModuleId = "focus" | "alarms" | "journeys" | "flow-configuration" | "quotas" | "trusted-advisor" | "support" | "access" | "audit" | "customers" | "platform";

export interface ModuleSection {
  id: string;
  label: string;
  /** Not built yet: the page says what it will be rather than 404ing. */
  planned?: boolean;
}

export interface Module {
  id: ModuleId;
  label: string;
  /** The path with no environment; `environmentScoped` modules append `/<environmentId>`. */
  path: string;
  environmentScoped: boolean;
  /** Also has a page with no environment - every customer at once - for staff (Trusted Advisor's list). */
  crossCustomer?: boolean;
  group: "modules" | "organization" | "gairm";
  sections?: ModuleSection[];
  /** Who sees it in the nav. Staff viewing a customer's portal (ADR-0011) count as the customer here. */
  staffOnly?: boolean;
  customerOnly?: boolean;
}

export const JOURNEY_SECTIONS: ModuleSection[] = [
  { id: "overview", label: "Overview" },
  { id: "contacts", label: "Contacts" },
  { id: "paths", label: "Paths" },
  { id: "concurrency", label: "Concurrency" },
  { id: "rewind", label: "Rewind", planned: true },
  { id: "observability", label: "Observability", planned: true },
];

export const FLOW_CONFIGURATION_SECTIONS: ModuleSection[] = [
  { id: "profiles", label: "Profiles" },
  { id: "aliases", label: "Aliases" },
  { id: "promotions", label: "Promotions" },
  { id: "cache", label: "Connect cache" },
];

export const MODULES: Module[] = [
  { id: "focus", label: "Focus", path: "/", environmentScoped: false, group: "modules" },
  { id: "alarms", label: "Alarms", path: "/alarms", environmentScoped: true, group: "modules" },
  { id: "journeys", label: "Journeys", path: "/journeys", environmentScoped: true, group: "modules", sections: JOURNEY_SECTIONS },
  { id: "flow-configuration", label: "Flow configuration", path: "/flow-configuration", environmentScoped: true, group: "modules", sections: FLOW_CONFIGURATION_SECTIONS },
  { id: "quotas", label: "Service quotas", path: "/quotas", environmentScoped: true, group: "modules" },
  { id: "trusted-advisor", label: "Trusted Advisor", path: "/trusted-advisor", environmentScoped: true, crossCustomer: true, group: "modules" },
  { id: "support", label: "Support cases", path: "/support", environmentScoped: true, group: "modules" },
  { id: "access", label: "Access", path: "/access", environmentScoped: true, group: "organization" },
  { id: "audit", label: "Audit", path: "/audit", environmentScoped: true, group: "organization" },
  { id: "customers", label: "Customers", path: "/customers", environmentScoped: false, group: "gairm", staffOnly: true },
  { id: "platform", label: "Platform", path: "/platform", environmentScoped: false, group: "gairm", staffOnly: true },
];

/** A customer sees their environments under Organization; the same list page staff reach as Customers. */
export const ENVIRONMENTS_MODULE: Module = { id: "customers", label: "Environments", path: "/customers", environmentScoped: false, group: "organization", customerOnly: true };

export function moduleById(id: ModuleId): Module {
  return MODULES.find((m) => m.id === id)!;
}

/** The path a nav link takes: the module's, with the environment the person last looked at where the module needs one. */
export function modulePath(module: Module, environmentId: string | undefined, section?: string): string {
  if (!module.environmentScoped || !environmentId) return module.path;
  const base = `${module.path}/${encodeURIComponent(environmentId)}`;
  return section ? `${base}/${section}` : base;
}

/**
 * Section 13.1: the environment a module opens on from the rail - the
 * person's focus environment for it if they set one, else the one they
 * last looked at. A focus environment they can no longer see is ignored.
 */
export function focusEnvironment(module: Module, preferences: Pick<UserPreferences, "focusEnvironments">, visible: ReadonlySet<string> | undefined, last: string | undefined): string | undefined {
  const chosen = preferences.focusEnvironments[module.id];
  if (chosen && (!visible || visible.has(chosen))) return chosen;
  return last;
}

/** Whether the person is looking at the portal as a customer would - their own role, or staff inside a view-as tab. */
export function actsAsCustomer(me: MeResponse | undefined): boolean {
  return me?.role === "customer" || Boolean(me?.viewAs);
}

export function visibleModules(me: MeResponse | undefined): Module[] {
  const asCustomer = actsAsCustomer(me);
  const list = MODULES.filter((m) => (m.staffOnly ? !asCustomer : true));
  return asCustomer ? [...list.filter((m) => m.group !== "gairm"), ENVIRONMENTS_MODULE] : list;
}

/** The old per-customer tab addresses, kept as redirects so a bookmark or a link in a case still lands. */
export const LEGACY_TABS: Record<string, { module: ModuleId; section?: string }> = {
  alarms: { module: "alarms" },
  quotas: { module: "quotas" },
  "trusted-advisor": { module: "trusted-advisor" },
  support: { module: "support" },
  concurrency: { module: "journeys", section: "concurrency" },
  journeys: { module: "journeys", section: "overview" },
  "flow-config": { module: "flow-configuration", section: "profiles" },
  access: { module: "access" },
  audit: { module: "audit" },
};
